"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Image from "next/image";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import OutboundLink from "@/components/global/links/outbound";
import { useCachedFetch } from "@/hooks/useCachedFetch";
const DevToEmbed = t0 => {
  const $ = _c(43);
  const {
    id,
    width: t1,
    layout: t2
  } = t0;
  const width = t1 === undefined ? "default" : t1;
  const layout = t2 === undefined ? "left" : t2;
  const t3 = `devto:${id}`;
  let t4;
  if ($[0] !== t3) {
    t4 = {
      cacheKey: t3
    };
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    data,
    loading,
    error
  } = useCachedFetch(`https://dev.to/api/articles/${id}`, undefined, t4);
  let t5;
  if ($[2] !== layout || $[3] !== width) {
    t5 = t6 => {
      const {
        children,
        className
      } = t6;
      return <div className={cn("not-prose bg-white dark:bg-neutral-900 rounded-lg border border-neutral-300 dark:border-neutral-700", "flex flex-col text-white font-inherit gap-2 p-4", className, {
        "w-full": width === "full",
        "max-w-md": width === "default",
        "mx-auto": layout === "center",
        "ml-auto": layout === "right",
        "mr-auto": layout === "left"
      })}>{children}</div>;
    };
    $[2] = layout;
    $[3] = width;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const Container = t5;
  if (loading) {
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex items-start gap-4">{t6}<div className="flex-1"><Skeleton className="h-6 w-3/4 mb-1" /><Skeleton className="h-4 w-1/2" /></div></div>;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    let t8;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex gap-2 mt-0.5"><Skeleton className="h-6 w-16" /><Skeleton className="h-6 w-16" /><Skeleton className="h-6 w-16" /></div>;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    let t9;
    if ($[8] !== Container) {
      t9 = <Container>{t7}{t8}</Container>;
      $[8] = Container;
      $[9] = t9;
    } else {
      t9 = $[9];
    }
    return t9;
  }
  if (error || !data) {
    const t6 = error || "Article not found.";
    let t7;
    if ($[10] !== t6) {
      t7 = <div className="flex items-center justify-center h-20 text-red-400 italic">{t6}</div>;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== Container || $[13] !== t7) {
      t8 = <Container>{t7}</Container>;
      $[12] = Container;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  const title = data.title;
  const author = data.user.name;
  const date = data.readable_publish_date;
  let t6;
  if ($[15] !== data.tag_list || $[16] !== data.tags) {
    t6 = Array.isArray(data.tags) ? data.tags : typeof data.tag_list === "string" ? data.tag_list.split(",") : [];
    $[15] = data.tag_list;
    $[16] = data.tags;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const tags = t6;
  let t7;
  if ($[18] !== author || $[19] !== data.user.profile_image_90) {
    t7 = data.user.profile_image_90 ? <Image src={data.user.profile_image_90} alt={author} width={48} height={48} className="w-full h-full object-cover" /> : author.charAt(0);
    $[18] = author;
    $[19] = data.user.profile_image_90;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden">{t7}</div>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== title) {
    t9 = <span className="text-xl font-bold leading-tight text-foreground">{title}</span>;
    $[23] = title;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== author || $[26] !== date) {
    t10 = <div className="text-muted-foreground text-sm mt-0.5">{author} · {date}</div>;
    $[25] = author;
    $[26] = date;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t8) {
    t12 = <div className="flex items-start gap-4">{t8}{t11}</div>;
    $[31] = t11;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== tags) {
    t13 = tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{tags.map(_temp)}</div>;
    $[34] = tags;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== data.url || $[37] !== t12 || $[38] !== t13) {
    t14 = <OutboundLink target="_blank" className="w-full p-4" href={data.url}>{t12}{t13}</OutboundLink>;
    $[36] = data.url;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== Container || $[41] !== t14) {
    t15 = <Container className="p-0">{t14}</Container>;
    $[40] = Container;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};
export default DevToEmbed;
function _temp(tag) {
  return <Badge variant="secondary" key={tag.trim()}>#{tag.trim()}</Badge>;
}